import React from 'react';
import { coreSync } from '../../lib/core';

// Recorded attendance, on a calendar event.
//
// A glyph, never a fill (`research/mms-capture/SPEC.md` §2.3: "Recorded
// attendance renders a status glyph on the calendar event"). The fill is the
// category's (`categoryPaint` in eventPaint.js); the two are separate channels.
//
// Three colours, not one per status: "the lesson happened", "it did not", and
// "it was called off" — what a teacher scanning a week wants at a glance.
// Every state is in full in the attendance drawer and on the popover. So a new
// status needs no rule here: the colour comes from the status's meta, and the
// fill from `billable`.
//
// Unrecorded draws nothing: it is the common case, and a dot on every event
// would say only "these are events".
//
// One shape, two channels: the colour is the kind of thing that happened, and
// whether the dot is filled or a ring is whether the family is charged (#158,
// argued below `glyphColour`).
//
// The colours are hexes rather than tokens because they are ink-like signal on
// a chip whose colour the studio chose, and a `color-mix` against any of eight
// swatches does not stay legible. `scripts/check-theme-tokens.mjs` reads only
// `src/**/*.css`, so it does not see colours written in a `.jsx`.
const CANCELLED = '#9ca3af';
const HAPPENED = '#16a34a';
const DID_NOT = '#ea580c';

export function glyphColour(status) {
  const meta = coreSync().resolveAttendanceStatus(status);
  // This arm selects `absent_notice_given`, `teacher_cancelled` and
  // `teacher_cancelled_issue_credit` (`core/src/attendance.rs`), and the last
  // is `billable: true` while the others are not, so the colour alone cannot
  // say whether a charge exists (#158); the fill/ring channel does.
  //
  // MMS draws all three as the same red circled ✕ before the student's name,
  // on an unchanged category fill, and distinguishes them only in the
  // attendance drawer's dropdown, which captions each `Billable` / `Not
  // Billable`.
  if (meta.teacherCancelled || status === 'absent_notice_given') return CANCELLED;
  // `meta.attended`, not `status.startsWith('present')` (#350).
  return meta.attended ? HAPPENED : DID_NOT;
}

// ── The money is a second channel, and a knowing difference from MMS (#158) ──
//
// MMS's calendar collapses these three statuses into the red circled ✕ it
// also uses for Absent, so there is nothing to copy. Instead: a filled dot
// means the family is charged for this lesson, a hollow ring that they are
// not — `billable` from the one table in `core/src/attendance.rs`, applied to
// every status, so it is a rule a teacher learns once. No fourth colour (#130).
//
//   Teacher Cancelled                        grey, hollow — nothing is owed
//   Teacher Cancelled, Issue Make-Up Credit  grey, FILLED — the family is charged
//
// The mark does not carry the money alone:
//
//   * `title` and `aria-label` name the status and its billability, for a
//     hover or a screen reader.
//   * `EventPopover` prints the per-lesson figure, gated on `isBillableFor`
//     (#159, #171, #362).
//
// `absent_notice_given` and `teacher_cancelled` stay identical — grey and
// hollow: two facts with the same money, and MMS conflates them too. The
// `title` names them apart.
//
// Whether a 7px ring reads as a ring is unchecked in a browser (#158). The
// ring is the secondary cue: the words and the popover figure carry the fact,
// and the fix would be a size or a shape.
const RING_PX = 2;

// #362's `This event requires a make-up credit` moves only `Unrecorded`'s
// billing (`is_billable_for` in `core/src/attendance.rs`), and this glyph draws
// nothing for `unrecorded`, so the flag cannot change what is drawn and the
// occurrence is not passed in.

export default function StatusGlyph({ status, size = 7 }) {
  if (!status || status === 'unrecorded') return null;
  const meta = coreSync().resolveAttendanceStatus(status);
  const colour = glyphColour(status);
  // The sentence MMS puts under each option in its own picker, on the mark.
  // `attendanceOptionLabel` writes the same parts as an option label; they are
  // assembled separately so neither acquires the other's punctuation.
  const label = `${meta.label} — ${meta.billable ? 'Billable' : 'Not Billable'}`;
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        borderRadius: '50%',
        // Filled for a charge, hollow for none. `boxSizing` so the ring eats
        // into the dot rather than growing it, which would be a second,
        // accidental channel.
        boxSizing: 'border-box',
        background: meta.billable ? colour : 'transparent',
        border: meta.billable ? 'none' : `${RING_PX}px solid ${colour}`,
        flexShrink: 0,
      }}
    />
  );
}
